/**
 * Realtime do Atendimento (Fase 3) — assinaturas Supabase Realtime.
 *
 * O front assina postgres_changes com a anon key + JWT do usuário; o Supabase
 * autoriza cada linha pela RLS (policies de SELECT client-scoped no schema `atendimento`).
 * Só LEITURA chega por aqui — as escritas continuam via backend.
 *
 * Três canais:
 *   - conversas do client (INSERT/UPDATE) → atualiza a lista do inbox;
 *   - mensagens de uma conversa (INSERT/UPDATE) → timeline ao vivo + recibos;
 *   - "digitando" via broadcast efêmero (não persiste em tabela).
 */

import { supabase } from '../../lib/supabaseClient.js'

/** Garante que o Realtime usa o token do usuário logado (autorização RLS). */
export async function ensureRealtimeAuth() {
  const { data } = await supabase.auth.getSession()
  const token = data?.session?.access_token
  if (token) supabase.realtime.setAuth(token)
  return !!token
}

/** Mudanças nas conversas do client (nova conversa, preview, status, unread). */
export function subscribeConversations(clientId, handler) {
  const ch = supabase
    .channel(`at-convs-${clientId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'atendimento', table: 'conversations', filter: `client_id=eq.${clientId}` },
      handler,
    )
    .subscribe()
  return () => { try { supabase.removeChannel(ch) } catch { /* noop */ } }
}

/** Mensagens de uma conversa: INSERT (nova) e UPDATE (recibo/edição). */
export function subscribeMessages(conversationId, { onInsert, onUpdate }) {
  const ch = supabase
    .channel(`at-msgs-${conversationId}`)
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'atendimento', table: 'messages', filter: `conversation_id=eq.${conversationId}` },
      (p) => onInsert?.(p.new),
    )
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'atendimento', table: 'messages', filter: `conversation_id=eq.${conversationId}` },
      (p) => onUpdate?.(p.new),
    )
    .subscribe()
  return () => { try { supabase.removeChannel(ch) } catch { /* noop */ } }
}

/**
 * Indicador "digitando" (broadcast efêmero). Devolve { notifyTyping, unsubscribe }.
 * notifyTyping() dispara um evento p/ os outros participantes; onTyping recebe o
 * userId de quem está digitando (auto-expira no consumidor após ~3s).
 */
export function subscribeTyping(conversationId, userId, onTyping) {
  const ch = supabase.channel(`at-typing-${conversationId}`, {
    config: { broadcast: { self: false } },
  })
  ch.on('broadcast', { event: 'typing' }, ({ payload }) => {
    if (payload?.userId && payload.userId !== userId) onTyping?.(payload.userId)
  }).subscribe()

  let last = 0
  const notifyTyping = () => {
    const now = Date.now()
    if (now - last < 1500) return // throttle
    last = now
    ch.send({ type: 'broadcast', event: 'typing', payload: { userId, at: now } })
  }
  return { notifyTyping, unsubscribe: () => { try { supabase.removeChannel(ch) } catch { /* noop */ } } }
}
